import I18n from 'I18n';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UISelect from 'UIComponents/input/UISelect';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import { InputGroup } from 'foundations-components/transitional/components/InputGroup';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Small } from 'foundations-components/transitional/components/Small';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { useCallback, useMemo, useState } from 'react';
import debounce from 'react-utils/debounce';
import useBlogRSSFeedDataState from '../../hooks/useBlogRssFeedDataState';
import useRSSFeedValidation from '../../hooks/useRSSFeedValidation';
import RSSFeedList from './RSSFeedList';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PROTOL_OPTIONS = [{
  text: 'https://',
  value: 'https://'
}, {
  text: 'http://',
  value: 'http://'
}];
export default function RSSFeedSelection() {
  const [feedProtocol, setFeedProtocol] = useState(PROTOL_OPTIONS[0].value);
  const [feedUrl, setFeedUrl] = useState('');
  const [feedList, setFeedList] = useState([]);
  const [feedValidationMessage, setFeedValidationMessage] = useState('');
  const {
    setRssFeedUrl
  } = useBlogRSSFeedDataState();
  const {
    called,
    loading,
    validate,
    validationResult
  } = useRSSFeedValidation({
    onCompleted: result => {
      if (result.feedResultStatus === 'FEED_OK') {
        setRssFeedUrl(`${feedProtocol}${feedUrl}`);
      } else if (result.feedResultStatus === 'FOUND_RSS_FEEDS') {
        setFeedList(result.feedList);
        setFeedValidationMessage(I18n.text('email-management-creation.createEmailPanel.blogRSS.rssFeed.feedsFoundForURL'));
      } else {
        setFeedValidationMessage(I18n.text('email-management-creation.createEmailPanel.blogRSS.rssFeed.feedUrlInvalid'));
      }
    }
  });
  const isFeedURLValid = useMemo(() => (validationResult === null || validationResult === void 0 ? void 0 : validationResult.feedResultStatus) === 'FEED_OK', [validationResult]);
  const hasError = useMemo(() => !loading && called && !isFeedURLValid, [called, isFeedURLValid, loading]);
  const onChangeProtocol = useCallback(event => {
    const protocol = event.target.value;
    setFeedProtocol(protocol);
    if (feedUrl.trim()) {
      void validate(`${protocol}${feedUrl}`);
    }
  }, [feedUrl, validate]);
  const debouncedValidate = useMemo(() => debounce(url => void validate(url), 500), [validate]);
  const onSelectFeed = useCallback((url, {
    debounce: _debounce
  } = {}) => {
    setFeedList([]);
    const urlWithoutProtocol = url.trim().replace(/http(s?):\/\//, '');
    setFeedUrl(urlWithoutProtocol);
    if (_debounce) {
      debouncedValidate(`${feedProtocol}${urlWithoutProtocol}`);
    } else {
      validate(`${feedProtocol}${urlWithoutProtocol}`);
    }
  }, [debouncedValidate, feedProtocol, validate]);
  const onChangeFeedUrl = useCallback(event => onSelectFeed(event.target.value, {
    debounce: true
  }), [onSelectFeed]);
  return /*#__PURE__*/_jsxs("div", {
    "data-test-id": "rss-feed-selection",
    children: [/*#__PURE__*/_jsxs(Grid, {
      children: [/*#__PURE__*/_jsx(GridItem, {
        size: 11,
        children: /*#__PURE__*/_jsxs(InputGroup, {
          label: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: "email-management-creation.createEmailPanel.blogRSS.rssFeed.label"
          }),
          use: "itemLeft",
          required: true,
          error: hasError,
          validationMessage: feedValidationMessage,
          children: [/*#__PURE__*/_jsx(FormControl, {
            children: /*#__PURE__*/_jsx(UISelect, {
              "data-test-id": "url-field-protocol",
              options: PROTOL_OPTIONS,
              defaultValue: feedProtocol,
              clearable: false,
              value: feedProtocol,
              onChange: onChangeProtocol
            })
          }), /*#__PURE__*/_jsx(FormControl, {
            children: /*#__PURE__*/_jsx(TextInput, {
              "data-test-id": "url-field-value",
              name: "rss_url",
              value: feedUrl,
              onChange: onChangeFeedUrl
            })
          })]
        })
      }), /*#__PURE__*/_jsx(GridItem, {
        size: 1,
        paddingBlockStart: "800",
        children: loading && /*#__PURE__*/_jsx(LoadingSpinner, {
          grow: true
        })
      })]
    }), isFeedURLValid && /*#__PURE__*/_jsx(Small, {
      use: "help",
      weight: "medium",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "email-management-creation.createEmailPanel.blogRSS.rssFeed.feedUrlValid"
      })
    }), feedList.length > 0 && /*#__PURE__*/_jsx(RSSFeedList, {
      feeds: feedList,
      onSelectFeed: onSelectFeed
    })]
  });
}